import { create } from "zustand";
import {
  clampSoilUnitPrefs,
  DEFAULT_SOIL_UNITS,
  parseSoilUnitsWire,
  readUserSoilLens,
  type SoilUnitPrefs,
} from "../lib/soilUnits";
import { api } from "../api";

// Stored soil values stay Mehlich-3 at 8 inches. This is a display lens applied when a number
// becomes text, never an input to arithmetic. Every factor is the ingest conversion read backwards
// a missing factor shows the stored value labelled M3.

const KEY = "sm.soilUnits.v1";

function load(): SoilUnitPrefs {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return DEFAULT_SOIL_UNITS;
    const p = JSON.parse(raw) as Partial<SoilUnitPrefs>;
    // Validate rather than spread: a stale or hand-edited value must not silently become the lens
    // through which every number on screen is read.
    return clampSoilUnitPrefs(p);
  } catch {
    return DEFAULT_SOIL_UNITS;
  }
}

export interface SoilUnitsState extends SoilUnitPrefs {
  set: (patch: Partial<SoilUnitPrefs>) => void;
  reset: () => void;
  /*
   *
   * Adopt the server's stored lens (BE-Light serves it on `/users/me`). Applied ONCE and only if the user
   * hasn't changed the lens this session — a late identity read must not silently undo a choice they just
   * made and are already reading numbers through.
   */
  hydrateFromServer: (raw: unknown) => void;
  /*
   *
   * Order-safe: applied only after /users/me has ANSWERED (a default must never race the user's own
   * record), and re-evaluated on org switch.
   */
  hydrateOrgDefault: (raw: unknown) => void;
  /* * the user has set the lens in THIS session, so the server value is stale by definition */
  touched: boolean;
  /* * where the active lens came from — the panel says “your organization's default” honestly */
  source: "user" | "org" | "system";
}

let serverHasMassBasis = false;
// the org-default gate's memory (module-level like serverHasMassBasis — not UI state):
let userLensKnown = false; // /users/me has answered (with or without a lens)
// A lens is the 4 unit keys from parseSoilUnitsWire, never the mere presence of a preferences
// object. A new account's /users/me can carry other keys (easy mode, terrain) and still have no
// lens, and the org default must still apply. The localStorage mirror is account-agnostic, so a
// shared browser must not treat the previous user's mirror as this user's lens.
let userHadLens = false;
let pendingOrgDefault: SoilUnitPrefs | null = null;
let orgDefaultKnown = false;

/* * test hook — the module-level gate memory outlives store resets */
export function resetSoilUnitsGateForTests(): void {
  userLensKnown = false;
  userHadLens = false;
  pendingOrgDefault = null;
  orgDefaultKnown = false;
}

export const useSoilUnits = create<SoilUnitsState>((set, get) => {
  // the resolver both hydrators funnel through: applies (or clears) the org default exactly when
  // the user has no lens of their own — idempotent, so arrival order cannot matter
  const applyOrgDefaultIfEligible = () => {
    // PERMANENT observability — live repro resisted two rounds of static analysis: every
    // resolver decision is one debug line — filter the console on [org-units] to see the story.
    const why = !userLensKnown
      ? "waiting: /users/me not answered"
      : !orgDefaultKnown
        ? "waiting: org default not fetched"
        : get().touched
          ? "blocked: touched this session"
          : userHadLens
            ? "blocked: user has their own lens"
            : pendingOrgDefault
              ? "APPLYING org default"
              : get().source === "org"
                ? "clearing: org has no default"
                : "no-op: no org default, nothing to clear";
    console.debug("[org-units] resolve:", why, {
      userLensKnown,
      orgDefaultKnown,
      touched: get().touched,
      userHadLens,
      pendingOrgDefault,
    });
    if (!userLensKnown || !orgDefaultKnown) return; // wait for BOTH answers
    if (get().touched || userHadLens) return;
    if (pendingOrgDefault) {
      set({ ...pendingOrgDefault, source: "org" });
    } else if (get().source === "org") {
      // switched to an org with no default while still on another org's — back to system defaults
      set({ ...DEFAULT_SOIL_UNITS, source: "system" });
    }
  };
  return {
    ...load(),
    touched: false,
    source: "system", // provisional until /users/me answers; the resolver never runs before it does
    hydrateOrgDefault: (raw) => {
      pendingOrgDefault = parseSoilUnitsWire(raw);
      console.debug("[org-units] org default fetched:", {
        raw,
        parsed: pendingOrgDefault,
      });
      orgDefaultKnown = true;
      applyOrgDefaultIfEligible();
    },
    hydrateFromServer: (raw) => {
      userLensKnown = true;
      console.debug("[org-units] /users/me lens:", {
        raw,
        hasLens: parseSoilUnitsWire(raw) != null,
      });
      // ONLY the four unit keys count — the preferences payload carries unrelated prefs, and a new
      // account's object must read as “no lens” — live repro.
      userHadLens = parseSoilUnitsWire(raw) != null;
      const read = readUserSoilLens(raw, get().massBasis);
      if (get().touched) return;
      if (!userHadLens || !read) {
        applyOrgDefaultIfEligible();
        return;
      } // no lens of their own — the ORG default may stand in
      if (read.sawMassBasis) serverHasMassBasis = true;
      set({ ...read.prefs, source: "user" });
      try {
        localStorage.setItem(KEY, JSON.stringify(read.prefs));
      } catch {
        /* cache only */
      }
    },
    set: (patch) => {
      const next = {
        depthIn: get().depthIn,
        pExtractant: get().pExtractant,
        kExtractant: get().kExtractant,
        massBasis: get().massBasis,
        ...patch,
      };
      set({ ...next, touched: true, source: "user" });
      try {
        localStorage.setItem(KEY, JSON.stringify(next));
      } catch {
        /* private mode — the preference is still live for this session */
      }
      // localStorage is the pre-hydration CACHE; the server is the record, because a lens that
      // differs per device shows the same field's P ~35% apart with nothing on screen to say so.
      // `mass_basis` rides the PATCH only once the server has shown it knows the key (it appeared on
      // /users/me).
      void api
        .updateSoilUnits({
          depth_in: next.depthIn,
          p_extractant: next.pExtractant,
          k_extractant: next.kExtractant,
          ...(serverHasMassBasis ? { mass_basis: next.massBasis } : {}),
        })
        .catch((e) =>
          console.warn("[soil units] not synced to your account yet", e),
        );
    },
    reset: () => {
      set({ ...DEFAULT_SOIL_UNITS, touched: true, source: "user" });
      void api
        .updateSoilUnits({
          depth_in: DEFAULT_SOIL_UNITS.depthIn,
          p_extractant: "m3",
          k_extractant: "m3",
          ...(serverHasMassBasis ? { mass_basis: "ppm" as const } : {}),
        })
        .catch((e) =>
          console.warn("[soil units] not synced to your account yet", e),
        );
      try {
        localStorage.removeItem(KEY);
      } catch {
        /* nothing to clean up */
      }
    },
  };
});

/* * The current preference, for the pure formatters. */
export const soilUnits = (): SoilUnitPrefs => {
  const s = useSoilUnits.getState();
  return {
    depthIn: s.depthIn,
    pExtractant: s.pExtractant,
    kExtractant: s.kExtractant,
    massBasis: s.massBasis,
  };
};
